import type { PrimitiveProps } from 'reka-ui';
import type { FunctionalComponent, VNode, VNodeArrayChildren } from 'vue';
import { Primitive } from 'reka-ui';
import { Comment, Fragment, h, isVNode } from 'vue';

interface Props extends PrimitiveProps {
	overrideChildCount?: number;
}

export const Grid: FunctionalComponent<Props> = (props, { slots, attrs }) => {
	const content = getChildren(slots.default?.() || []);
	const containsGrid = content.some((vnode) => isVNode(vnode) && vnode.type as any === Grid);
	// Cheat with a second div to get the parent variables until https://github.com/w3c/csswg-drafts/issues/2864 is available
	return h('div', {
		class: 'contents gap-[inherit]',
		style: {
			'--dgrid-parent-cols': 'var(--dgrid-cols, 12)',
			'--dgrid-parent-children': 'var(--dgrid-children, 1)',
			'--dgrid-parent-col-span': 'var(--dgrid-col-span, 1)',
		},
	}, [
		h(Primitive, {
			as: props.as,
			asChild: props.asChild,
			...attrs,
			class: [
				'col-span-(--dgrid-col-span)',
				{
					'grid grid-cols-[repeat(var(--dgrid-cols),minmax(0,1fr))] gap-[inherit]': containsGrid,
				},
				attrs.class,
			],
			style: {
				...attrs.style as any,
				'--dgrid-children': props.overrideChildCount ?? content.length,
				'--dgrid-col-span': 'calc(var(--dgrid-parent-cols) / var(--dgrid-parent-children))',
				'--dgrid-cols': 'var(--dgrid-col-span)',
			},
		}, () => content),
	]);
};

Grid.props = ['as', 'asChild', 'overrideChildCount'];

Grid.inheritAttrs = false;

function getChildren(children: VNodeArrayChildren): VNodeArrayChildren {
	const result: VNodeArrayChildren = [];

	for (const child of children) {
		if (isVNode(child)) {
			if (child.type === Fragment) {
				result.push(...getChildren(getFragmentChildren(child)));
			}
			else if (child.type !== Comment) {
				result.push(child);
			}
		}
	}

	return result;
}

function getFragmentChildren(fragment: VNode | VNodeArrayChildren): VNodeArrayChildren {
	if (Array.isArray(fragment)) {
		return fragment;
	}

	if (Array.isArray(fragment.children)) {
		return fragment.children;
	}

	return [];
}

export default Grid;
